// Command Center — Amenity Payouts (cross-company payout console over PLATFORM-collect sites).
// Copy mirrors the approved mockup (payouts-mockup.html). Distinct module from amenityPayments.ts:
// that page is per-company payment SETUP; this one is the super admin's money console.
export default {
    navTitle: 'Amenity Payouts',
    ccTitle: 'Amenity Payouts',
    // Tabs of the Command Center "Amenity payments" page (PM spec: payouts first, setup second).
    tabPayouts: 'Payouts',
    tabPaymentSetup: 'Payment setup',
    ccSubtitle:
        'Properties where Swiftlane collects on their behalf. Money we are holding, and what we have sent. ' +
        'Properties on their own Stripe account are paid by Stripe directly and never appear here.',
    loadError: 'Could not load payout balances.',
    retry: 'Retry',
    empty: 'No properties are collecting through Swiftlane yet.',

    // ----- header tiles -----
    tileBalanceOwed: 'Balance owed',
    tileBalanceOwedNote: (n: number): string => `across ${n} ${n === 1 ? 'property' : 'properties'}`,
    tileReady: 'Ready to pay',
    tileNeedsReview: 'Needs review',
    // REVIEW_FIXES F4: nothing is blocked — say what to look at.
    tileNeedsReviewNote: (n: number): string => `${n} ${n === 1 ? 'property' : 'properties'} — open dispute or unmeasured fees`,
    tileOldestUnpaid: 'Oldest unpaid',
    // REVIEW_FIXES F4 (UX C-6): the tile is period-scoped, so the label names the selected month.
    // Staging UX review #11: abbreviated — the segmented control owns the one full month name.
    tilePaidInPeriod: (period: string): string => `Paid · ${period}`,
    tilePaidThisPeriodNote: (n: number): string => `${n} ${n === 1 ? 'payout' : 'payouts'}`,
    // F10: negatives are called out beside the positives-only headline, never netted into it.
    tileBalanceOwedNegatives: (negative: string, m: number): string => `${negative} carried as negative by ${m}`,
    // F4: eyebrows separating the all-time tiles from the period-scoped one.
    tileScopeAllTime: 'All time',
    // UX review #11: the band's period eyebrow stops repeating the month — the seg control names it.
    tileScopePeriod: 'This period',
    tileOnOwnStripe: 'On own Stripe',
    tileOnOwnStripeNote: 'paid directly, not shown',

    // ----- list toolbar + table -----
    searchPlaceholder: 'Search property or company',
    sortNote: 'Sorted for a payout run — largest payable first, exceptions below',
    customPeriod: 'Custom',
    columnProperty: 'Property',
    columnStatus: 'Status',
    columnBalance: 'Balance (all-time)',
    columnGross: 'Gross captured',
    columnReturned: 'Returned',
    columnWithheld: 'Withheld',
    columnShare: 'Property share',
    columnOldestUnpaid: 'Oldest unpaid',
    columnLastPaid: 'Last paid',
    // UX review #8: the word, capitalized — a cell value, not a whispered footnote.
    neverPaid: 'Never',
    // REVIEW_FIXES F7: SETTLED means balance = 0 — it says nothing about activity.
    settledFootRow: (n: number): string => `${n} ${n === 1 ? 'property' : 'properties'} settled — nothing owed`,
    searchEmpty: (query: string): string => `No properties match '${query}'`,
    unmeasuredMarkTip: 'Stripe fee not yet measured',
    settledShow: 'Show',
    settledHide: 'Hide',
    statusReady: 'Ready to pay',
    statusSettled: 'Settled',
    statusNegative: 'Negative',
    statusNeedsReview: 'Needs review',
    // REVIEW_FIXES F3 — the reason rides on the chip: "Needs review · open dispute".
    reviewReasonOpenDispute: 'open dispute',
    reviewReasonUnmeasuredFees: 'unmeasured fees',
    reviewReasonPendingTransfer: 'pending transfer',
    statusUnknown: 'Unknown',
    statusUnknownTip: 'Could not compute — reload or contact engineering',
    // REVIEW_FIXES F7: ≤ 45 words.
    legend:
        "Balance is all-time and never moves with the report month. The month columns are that month's earnings. " +
        'A ~ marks a Stripe fee not yet measured. Parentheses mean a negative number.',

    // ----- property page -----
    backToList: '← All properties',
    collectsOnBehalf: 'Swiftlane collects on their behalf',
    owedLine: (amount: string): string => `${amount} owed`,
    exportButton: 'Export',
    recordManualPayout: 'Record manual payout',
    payViaStripe: (amount: string): string => `Pay ${amount} via Stripe`,
    // ----- Pay via Stripe (TRANSFER_SPEC.md §6.2, copy VERBATIM) -----
    // Disabled-button tooltips, one per transfer_blockers code. Each says why AND what to do next.
    blockerNoRecipient: "This property hasn't set up bank transfers. Email them the setup link, or record a manual payout.",
    blockerRecipientPending: "The property started Stripe setup but hasn't finished. Stripe still needs details from them.",
    blockerNegativeBalance: 'Nothing to pay — balance is zero or negative.',
    // Fix round 1 U3/T5: a PENDING Stripe row must be resolved before another transfer goes out.
    blockerPendingTransfer: 'A transfer is still confirming with Stripe. Retry or wait for it to settle before sending another.',
    blockerUnmeasuredFees: (n: number): string =>
        `${n} payment(s) still have no measured Stripe fee, so the balance may be too high. Measure fees now, then pay.`,
    // Shown only when the API sent no transfer_blockers at all (backend without the transfer build).
    payViaStripeUnavailable: 'Paying via Stripe is not available on this server yet. Record a manual payout instead.',
    measureFeesNow: 'Measure fees now',
    measureFeesResult: (measured: number, still: number): string => `Measured ${measured} · still unmeasured ${still}`,
    measureFeesFailed: 'Could not measure fees.',
    // Payout-method line under the owed amount + the chip everywhere (list, setup tab).
    payoutMethodStripeLine: (date: string): string => `Payout method: Stripe · connected ${date}`,
    payoutMethodStripeLineNoDate: 'Payout method: Stripe',
    payoutMethodPendingLine: 'Payout method: Bank setup pending — the property started Stripe setup but has not finished.',
    payoutMethodManualLine: 'Manual only — this property has not set up bank transfers.',
    emailSetupLink: 'Email setup link',
    emailSetupLinkSent: (n: number): string => `Sent to ${n} ${n === 1 ? 'admin' : 'admins'}`,
    // Fix round 1 U5.
    emailSetupLinkNoAdmins: (company: string): string => `No admins found for ${company} — nobody to send to.`,
    emailSetupLinkAgainIn: (minutes: number): string => `Sent · again in ${minutes} min`,
    // Fix round 1 U3: 202 — the PENDING row exists, Stripe has not confirmed yet.
    transferPending: 'Transfer sent — waiting for Stripe to confirm',
    emailSetupLinkRateLimited: (when: string): string => `Setup link already sent — try again after ${when}.`,
    emailSetupLinkAlreadyStripe: 'This property already receives payouts via Stripe.',
    emailSetupLinkFailed: 'Could not send the setup link.',
    columnPayoutMethod: 'Payout method',
    // Fix round 1 U1: short chips so the list fits at 1440px; the tooltips carry the long form.
    chipPayoutStripe: 'Stripe',
    chipPayoutPending: 'Setup pending',
    chipPayoutManual: 'Manual',
    chipPayoutStripeTip: 'Bank transfers via Stripe are live — "Pay via Stripe" sends the balance to their connected account.',
    chipPayoutPendingTip: 'The property started Stripe setup but has not finished; Stripe still needs details from them.',
    chipPayoutManualTip: 'No bank transfer setup. Pay by ACH/cheque/wire and record it, or email them the setup link.',
    payoutMethodNotApplicable: 'n/a',
    tileReadyStripeOnly: (stripe: string): string => `${stripe} via Stripe`,
    tileReadySplit: (stripe: string, manual: string): string => `${stripe} via Stripe · ${manual} manual`,

    // ----- transfer modal (D9 copy VERBATIM) -----
    transferModalTitle: (property: string, amount: string): string => `Pay ${property} ${amount} via Stripe`,
    transferModalSubtitle: (company: string, balance: string): string => `${company} · balance ${balance}`,
    transferIntro: "This moves money. Stripe transfers the amount from Swiftlane's balance to the property's connected account.",
    transferConfirmBody: (amount: string, property: string): string =>
        `Moves ${amount} from Swiftlane's Stripe balance to ${property}'s connected account. ` +
        'Stripe pays their bank on its normal schedule (typically 2 business days). This cannot be undone from here.',
    sendTransferButton: 'Send transfer',
    transferSuccess: (transferId: string): string => `Transfer sent — ${transferId} Balance updated.`,
    transferFailed: 'Could not send the transfer.',
    // 409/400/503 codes -> one human sentence each (§5.4).
    errTransferNoRecipient: "Refused: this property hasn't set up bank transfers. Email them the setup link, or record a manual payout.",
    errTransferRecipientPending: 'Refused: the property has not finished Stripe setup — Stripe still needs details from them.',
    errTransferUnmeasuredFees:
        'Refused: some payments still have no measured Stripe fee, so the balance may be too high. Measure fees now, then pay.',
    errTransferNotConfigured: 'Refused: Stripe is not configured in this environment, so no transfer can be sent.',
    errTransferKeyReused: 'Refused: this request key was already used for another property. Close and reopen the dialog to try again.',
    errTransferPartialReason: 'Refused: a reason is required for a partial transfer.',
    errTransferInvalid: 'Refused: the request was invalid. Reload and try again.',
    errTransferStripe: (stripeMessage: string): string => `Stripe refused the transfer: ${stripeMessage}`,
    errTransferStripeNothingSent: 'Nothing was sent; the failed attempt is recorded.',

    // ----- payout history: status chip + retry (§6.2) -----
    historyStatus: 'Status',
    // REVIEW_FIXES F5: who recorded it and why only part of it — the audit trail on the page.
    historyRecordedBy: 'Recorded by',
    historyPartialLine: (reason: string): string => `Partial: ${reason}`,
    historyMethodStripe: 'Stripe',
    historyStatusSettled: 'Settled',
    historyStatusPending: 'Pending — confirming with Stripe',
    historyStatusReversed: (amount: string): string => `Reversed ${amount}`,
    historyStatusFailed: 'Failed',
    historyReversedLine: (reversed: string): string => `(−${reversed} reversed)`,
    historyRecordedByLine: (who: string): string => `by ${who}`,
    historyRetry: 'Retry',
    retrySettled: (transferId: string): string => `Confirmed — ${transferId}. Balance updated.`,
    retryFailed: 'Stripe has no record of this transfer — it is now marked failed and no longer counts as paid.',
    retryError: 'Could not retry the transfer.',
    attentionPendingTransfer: 'Transfer pending confirmation',
    attentionPendingTransferDetail: 'tr_ not yet received',
    recordDisabledNothingOwed: 'Nothing to pay — the balance is zero or negative.',
    attentionTitle: (n: number): string => `Needs attention — ${n} ${n === 1 ? 'item' : 'items'}`,
    attentionOpenDispute: 'Open dispute',
    attentionUnmeasuredFees: 'Unmeasured processing fees',
    // REVIEW_FIXES F2 (P0): the figure is the GROSS charged on the unmeasured payments, not
    // missing fees — say so, and never bold it.
    attentionUnmeasuredDetail: (n: number, charged: string): string =>
        `${n} payment(s) (${charged} charged) have no measured Stripe fee yet. They count as $0 in the balance until measured — never estimated.`,
    attentionOrphanCapture: 'Orphan capture',
    detailLoadError: 'Could not load this property.',

    // ----- waterfall -----
    waterfallTitle: 'Why the balance is what it is',
    wfAllTime: 'All time',
    wfGross: 'Gross captured',
    wfRefunds: 'Refunds to residents',
    wfChargebacks: 'Chargebacks',
    wfProcessingFees: 'Stripe processing fees',
    wfChargebackFees: 'Chargeback fees',
    wfCommission: 'Swiftlane commission',
    // REVIEW_FIXES F7: a percentage, never "bps"; "clerkage" is a UK legal term.
    wfCommissionNote: (bps: number): string => `${bps / 100}% configured`,
    wfClerkage: 'Cancellation charges kept by Swiftlane',
    // Copy-for-email sentence (F1) — one clause per non-zero waterfall line, verbatim from the ruling.
    sentenceCharged: (period: string, gross: string): string => `${period}: residents were charged ${gross}.`,
    sentenceRefunds: (amount: string): string => `${amount} was refunded to residents`,
    sentenceChargebacks: (amount: string): string => `${amount} is held for an open chargeback`,
    sentenceProcessingFees: (amount: string): string => `${amount} went to Stripe processing fees`,
    sentenceChargebackFees: (amount: string): string => `${amount} went to chargeback fees`,
    sentenceCommission: (amount: string): string => `${amount} went to Swiftlane commission`,
    sentenceClerkage: (amount: string): string => `${amount} in cancellation charges was kept by Swiftlane`,
    sentenceNothingLeft: 'Nothing was refunded, disputed or withheld',
    sentenceShare: (share: string): string => `leaving ${share} as the property's share.`,
    sentencePaid: (paid: string): string => `Paid to date ${paid}.`,
    sentenceBalance: (balance: string): string => `Balance owed (all time): ${balance}.`,
    wfPropertyShare: 'Property share',
    wfPaidToDate: 'Paid out to date',
    wfBalance: 'Balance',
    wfBalanceNote: 'always all-time',
    // UX review #10: identical columns collapse to one; the header says why there is only one.
    wfSingleColumnTip: (month: string): string =>
        `${month} is all the activity to date, so the period and all-time columns are identical — shown once.`,
    // UX review #8: the period column's blanks at Paid/Balance are by design, not missing data.
    wfPeriodBlankTip: 'Balance and paid-to-date are always all-time — this column is blank by design.',
    copyForEmail: 'Copy for email',
    copied: 'Copied.',
    copyFailed: 'Could not copy — select the text instead.',

    // ----- payout history -----
    historyTitle: 'Payout history',
    historyDate: 'Date',
    historyMethod: 'Method',
    historyReference: 'Reference',
    historyAmount: 'Amount',
    historyEmpty: 'No payouts recorded yet.',

    // ----- bookings / evidence -----
    bookingsTitle: 'Where the money came from',
    bookingsSubtitle: 'one row per booking — click any row for the payment records behind it',
    bookingsSearchPlaceholder: 'Booking id, resident or unit',
    bookingsAllAmenities: 'All amenities',
    chipAll: 'All',
    chipRefunded: 'Refunded',
    chipDisputed: 'Disputed',
    chipUnmeasured: 'Unmeasured fee',
    bookingColumnBooking: 'Booking',
    bookingColumnWho: 'Amenity · resident',
    bookingColumnCharged: 'Charged',
    bookingColumnReturned: 'Returned',
    // REVIEW_FIXES F7: this column mixes Stripe's fee with Swiftlane's cancellation charge — say so.
    bookingColumnFees: 'Fees & charges',
    bookingColumnFeesTip: 'Stripe processing fee plus any cancellation charge kept by Swiftlane',
    bookingColumnShare: 'Share',
    bookingColumnState: 'State',
    bookingStateCaptured: 'Captured',
    bookingStateRefunded: 'Refunded',
    bookingStatePartRefund: 'Part refund',
    bookingStateDisputed: 'Disputed',
    movementsIntro: (id: string): string =>
        `Payment records behind booking ${id} — this is the evidence a property is sent when they query the number.`,
    movementWhen: 'When',
    movementKind: 'Movement',
    movementRef: 'Stripe reference',
    movementAmount: 'Amount',
    movementNote: 'Note',
    negativeShareNote:
        'A refunded booking has a negative share — Stripe keeps its fee whether or not the charge is reversed, ' +
        'and that cost sits with the property.',
    bookingsLoadError: 'Could not load bookings.',

    // ----- record payout modal -----
    recordModalTitle: 'Record a payout already sent',
    recordModalSubtitle: (company: string, balance: string): string => `${company} · balance ${balance}`,
    recordingOnly:
        'Recording only. This does not move any money. Use it when the payment was already sent by ACH, cheque or wire outside Swiftlane.',
    partialNotice: (carry: string): string => `Partial payout — ${carry} will carry forward to the next period. A reason is required.`,
    amountLabel: 'Amount',
    amountHint: 'Prefilled to the full balance. A smaller amount is allowed and carries the remainder forward.',
    partialReasonLabel: 'Reason for partial payout',
    partialReasonPlaceholder: 'e.g. trimmed to fit the ACH batch limit',
    methodLabel: 'Method',
    referenceLabel: 'Reference',
    referencePlaceholder: 'ACH trace / cheque no.',
    referenceHint: 'Ties this record to the bank statement.',
    periodLabel: 'Period covered',
    noteLabel: 'Note',
    notePlaceholder: 'Optional, 256 characters',
    periodAttributionNote: 'The period is attribution only — narrowing it does not change the amount paid.',
    continueButton: 'Continue',
    cancelButton: 'Cancel',
    backButton: 'Back',
    confirmTitle: 'Confirm payout',
    // REVIEW_FIXES F7: "Pay X … recorded only" contradicted itself — the verb is "Record".
    recordConfirmHeadline: (property: string): string => `Record payout to ${property}`,
    confirmPay: 'Pay',
    confirmVia: (method: string, reference: string): string => `by ${method}, ref ${reference} — recorded only, no money moves`,
    confirmWarn: 'This cannot be undone. There is no way to void a recorded payout.',
    retypeLabel: 'Re-type the amount to confirm',
    retypeHint: 'Required because this differs from the balance.',
    retypeMismatch: 'The re-typed amount does not match.',
    recordButton: 'Record payout',
    recordSuccess: (amount: string): string => `Recorded ${amount}. Balance updated.`,
    errAmountInvalid: 'Enter a positive amount with at most two decimals.',
    errAmountOverBalance: (owed: string): string => `More than the ${owed} owed — the server will refuse this.`,
    errReferenceRequired: 'A reference is required.',
    errPartialReasonRequired: 'A reason is required for a partial payout.',
    errNoteTooLong: 'Note is limited to 256 characters.',
    // REVIEW_FIXES F5/B3 — "Paid on": the day the money left, backdatable, never in the future.
    paidOnLabel: 'Paid on',
    paidOnHint: 'The day the money left. Defaults to today.',
    errPaidOnFuture: 'The paid-on date cannot be in the future.',
    errPaidOnTooEarly: 'The paid-on date cannot be before 2026-01-01.',
    errPaidOnFormat: 'Enter the paid-on date as YYYY-MM-DD.',
    errOverRemittance: (owed: string): string => `Refused: more than the ${owed} currently owed. Reload and retry.`,
    errNegativeBalance: 'Refused: the balance is zero or negative — there is nothing to pay.',
    recordFailed: 'Could not record the payout.',

    // ----- export modal -----
    exportTitle: (scope: string): string => `Export — ${scope}`,
    exportIntro:
        'Two files, because a bookkeeper and a disputing property need different things. Both open cleanly in Excel — ' +
        'amounts as plain 2dp decimals, ISO dates, ids as text so Excel cannot eat the precision.',
    exportRegisterTitle: 'Payout register',
    exportRegisterDesc: 'One row per payout. What you journal. paid_date, property, amount, method, reference, period, recorded_by.',
    exportLedgerTitle: 'Booking ledger',
    exportLedgerDesc:
        'One row per payment movement. The evidence file you attach when a property queries a number — booking, amenity, ' +
        'gross, refunded, disputed, processing fee (and whether it was measured), commission, clerkage, share, Stripe reference.',
    exportBothTitle: 'Both',
    exportBothDesc: 'Two CSV files, downloaded together. Recommended for month-end.',
    exportDownload: 'Download',
    exportFailed: 'Export failed.',
    exportDone: 'Download started.',

    // =====================================================================================
    // ACCOUNT-LEVEL redesign (ACCOUNTS_SPEC.md §F — labels verbatim). Level 1 = all accounts,
    // Level 2 = one account, Level 3 = the property page (unchanged + A.3 additions).
    // =====================================================================================
    accountsLoadError: 'Could not load accounts.',
    accountsEmpty: 'No accounts have amenity payments yet.',
    accountsSearchEmpty: (query: string): string => `No accounts match '${query}'`,
    accountsSearchPlaceholder: 'Search account or property',
    crumbAllAccounts: 'All accounts',
    backToAccount: '← Back',
    updatedAt: (time: string): string => `Updated ${time}`,
    refresh: 'Refresh',
    // ----- Level 1 KPI band (7 tiles) -----
    tileAcctBalanceOwedNote: (n: number, negative: string, m: number): string =>
        m > 0
            ? `across ${n} ${n === 1 ? 'account' : 'accounts'} · ${negative} carried negative by ${m}`
            : `across ${n} ${n === 1 ? 'account' : 'accounts'}`,
    tileAcctReadyNote: (stripe: string, manual: string): string => `${stripe} via Stripe · ${manual} manual`,
    tileAcctNeedsReviewNote: (n: number): string => `${n} ${n === 1 ? 'account' : 'accounts'} — open dispute or unmeasured fees`,
    tileAccounts: 'Accounts',
    tileAccountsNote: (owed: number, connectOnly: number, noActivity: number): string =>
        `${owed} owed · ${connectOnly} Stripe-direct only · ${noActivity} no activity`,
    tileChecks: 'Checks',
    tileChecksPassed: (n: number): string => `${n} passed`,
    tileChecksFailing: (n: number): string => `${n} failing`,
    stripePlatformBalance: (amount: string): string => `Stripe platform balance: ${amount}`,
    stripePlatformBalanceUnavailable: 'Stripe platform balance: — (cross-check not yet available)',
    // ----- Level 1 table -----
    columnAccount: 'Account',
    columnProperties: 'Properties',
    columnBalanceOwed: 'Balance owed',
    columnCarriedNegative: 'Carried negative',
    columnPeriodShare: (month: string): string => `${month} share`,
    // ACCOUNTS_FIXES_ROUND1 A1 — short enough to fit at 1440 with no truncation.
    columnPeriodVolume: 'Own-Stripe vol.',
    columnPeriodVolumeTip:
        "Gross Stripe charged on the account's own-Stripe properties this month — paid to them directly, never owed by Swiftlane.",
    balanceNegativeSubLine: (amount: string, n: number): string => `${amount} negative · ${n} ${n === 1 ? 'property' : 'properties'}`,
    // E.1 actionable_cents — the figure the payment-first sort ranks by, named on the row.
    actionablePayableLine: (amount: string): string => `${amount} payable`,
    checksShowPassed: (n: number): string => `Show ${n} passed`,
    checksHidePassed: 'Hide passed',
    journalColAmount: 'Amount',
    journalColFees: 'Fees & charges',
    journalColFeesTip: 'Stripe fee + Swiftlane commission + cancellation charge; hover a cell for the split.',
    journalFeeStripe: 'Stripe fee',
    journalFeeCommission: 'Commission',
    journalFeeClerkage: 'Cancellation charge',
    tileRollForwardOpening: (amount: string): string => `Opening ${amount}`,
    tileRollForwardClosing: (amount: string): string => `Closing ${amount}`,
    // UX review #6: the tile's headline is the closing figure; the caption names the opening.
    tileRollForwardFrom: (opening: string): string => `from ${opening} opening`,
    storyActionNotRefundFailed: 'Only a booking whose newest refund failed can be retried.',
    storyActionNothingRefundable: 'Nothing is left to refund on this booking.',
    storyActionConnect: 'Own-Stripe property — Swiftlane holds none of this money.',
    adminsCount: (n: number): string => `${n} ${n === 1 ? 'admin' : 'admins'}`,
    propertiesBreakdown: { platform: 'Swiftlane collects', connect: 'own Stripe', notSetUp: 'not set up' },
    negativeCarriedLine: (amount: string): string => `${amount} carried forward`,
    reviewCountLine: (n: number, reasons: string): string => `${n} ${reasons}`,
    computeErrorLine: (n: number): string => `${n} ${n === 1 ? 'property' : 'properties'} could not be computed — excluded`,
    hiddenAccountsFoot: (n: number): string => `${n} ${n === 1 ? 'account' : 'accounts'} with amenity payments off and no history — `,
    // UX review #8: the legend explains EVERY symbol on the page — the em-dash was the most-used
    // symbol and the only unexplained one.
    accountsLegend:
        'Balances are all-time. Period columns show that month only. Own-Stripe volume is money Stripe paid the ' +
        'property directly — never owed by Swiftlane. ~ = Stripe fee not yet measured. Parentheses = negative. ' +
        'n/a = does not apply. — = no data.',
    statusPaidByStripe: 'Paid by Stripe directly',
    statusNotCollecting: 'Not collecting',
    chipPayoutStripeDirect: 'Stripe-direct',
    chipPayoutStripeDirectTip: 'Own Stripe account — Stripe pays the property directly; Swiftlane never holds this money.',
    // ----- Checks panel (§C) -----
    checksTitle: 'Checks',
    checksAllPassed: (n: number, reconcile: string): string => `${n} checks passed · last fee reconcile ${reconcile}`,
    checksSummary: (failing: number, passed: number): string => `${failing} failing · ${passed} passed`,
    checksReconcileUnknown: 'not yet run',
    checkColumnId: 'Check',
    checkColumnScope: 'Scope',
    checkColumnSeverity: 'Severity',
    checkColumnDetail: 'Detail',
    checkColumnAmount: 'Amount',
    checkSeverityBlock: 'Block',
    checkSeverityWarn: 'Warn',
    checkSeverityInfo: 'Info',
    checkStatusPass: 'Pass',
    checkStatusFail: 'Fail',
    checkStatusNa: 'n/a',
    checksFailingDrawerTitle: (n: number): string => `${n} failing ${n === 1 ? 'check' : 'checks'}`,
    // ----- Level 2 header + KPIs -----
    accountLoadError: 'Could not load this account.',
    paymentsOn: 'Amenity payments on',
    paymentsOff: 'Amenity payments off',
    commissionRate: (percent: string): string => `Commission ${percent}%`,
    propertiesCount: (n: number): string => `${n} ${n === 1 ? 'property' : 'properties'}`,
    exportAccount: 'Export account',
    emailSetupLinkToManual: (n: number): string => `Email setup link to ${n} manual ${n === 1 ? 'property' : 'properties'}`,
    emailSetupLinkBulkDone: (admins: number, properties: number): string =>
        `Sent to ${admins} ${admins === 1 ? 'admin' : 'admins'} across ${properties} ${properties === 1 ? 'property' : 'properties'}`,
    payViaStripeBulk: (amount: string, n: number): string => `Pay ${amount} via Stripe (${n} ${n === 1 ? 'property' : 'properties'})`,
    payViaStripeBulkDisabled: 'No property of this account is ready for a Stripe transfer — see Payout method and Status.',
    payViaStripeBulkBlockedByChecks: 'A blocking check is failing for this account — resolve it in Checks before paying.',
    bulkPaySendNeedsRetype: 'Type the total above to enable sending.',
    tileL2BalanceOwedNote: (n: number, negative: string, m: number): string =>
        m > 0
            ? `${n} ${n === 1 ? 'property' : 'properties'} · ${negative} carried negative by ${m}`
            : `${n} ${n === 1 ? 'property' : 'properties'}`,
    tileRollForward: 'Roll-forward',
    tileRollForwardNote: (opening: string, closing: string): string => `Opening ${opening} → Closing ${closing}`,
    tileRollForwardView: 'view',
    tileNeedsReviewProperty: (property: string, reasons: string): string => (reasons ? `${property} — ${reasons}` : property),
    emptyAccountCopy: "Swiftlane holds none of this account's money. Volume shown is what Stripe charged for their own-Stripe properties.",
    // ----- Level 2 properties table (A.2) -----
    columnCollectsVia: 'Collects via',
    collectsViaPlatform: 'Swiftlane collects',
    collectsViaConnect: 'Own Stripe account',
    collectsViaNotSetUp: 'Not set up',
    columnStripeDestination: 'Stripe destination',
    destinationRevoked: (date: string): string => `revoked ${date}`,
    columnUnmeasured: 'Unmeasured',
    columnOpenDisputes: 'Open disputes',
    columnPendingTransfer: 'Pending transfer',
    // Fix round 1 residuals — short Level 2 headers so every header shows in full at 1440.
    columnBalanceShort: 'Balance',
    columnGrossShort: 'Gross',
    columnShareShort: 'Share',
    columnUnpaidShort: 'Unpaid',
    columnBalanceShortTip: 'All-time balance owed (never moves with the report month).',
    columnMethodShort: 'Method',
    columnMethodShortTip: 'Payout method — how this property gets paid (Stripe transfer, setup pending, or manual ACH/cheque/wire).',
    chipPending: 'Pending',
    goToPaymentSetup: 'Go to Payment setup',
    footerTotalsPlatform: 'Total — Swiftlane collects',
    notSetUpFootRow: (n: number): string => `${n} ${n === 1 ? 'property' : 'properties'} not set up — `,
    footerTotalsConnect: 'Own-Stripe volume — not owed',
    footerNegatives: (amount: string, n: number): string => `${amount} negative across ${n}`,
    // ----- Roll-forward (A.4) -----
    rfTitle: (month: string): string => `Roll-forward — ${month}`,
    rfOpening: 'Opening balance',
    rfOpeningNote: 'as at period start',
    rfGross: 'Gross collected',
    rfRefunds: 'Refunds to residents',
    rfChargebacks: 'Chargebacks (open + lost)',
    rfChargebacksWon: 'Chargebacks won',
    rfProcessingFees: 'Stripe processing fees (measured)',
    rfDisputeFees: 'Stripe dispute fees (measured)',
    rfCommission: 'Swiftlane commission (retained)',
    rfClerkage: 'Cancellation charges kept by Swiftlane',
    rfShare: 'Property share, period',
    rfPayouts: 'Payouts in period',
    rfClosing: 'Closing balance',
    rfClosingNote: 'as at period end',
    rfPositives: 'Positives',
    rfNegatives: 'Negatives',
    rfTotal: 'Total',
    rfMemoTitle: 'Memo — never in the arithmetic',
    rfMemoUnmeasured: (n: number, charged: string): string => `Unmeasured fees ${n} / ${charged} charged`,
    rfMemoOpenDisputes: (n: number, amount: string): string => `Open disputes ${n} / ${amount}`,
    rfMemoPending: (n: number, amount: string): string => `Pending transfers ${n} / ${amount}`,
    rfMemoFailed: (n: number): string => `Failed transfers ${n} (not counted)`,
    rfMemoReversed: (amount: string): string => `Reversed ${amount}`,
    rfByMethod: 'by method',
    rfByStatus: 'by status',
    rfTiesOk: 'Ties',
    rfTiesOff: (amount: string): string => `Off by ${amount}`,
    // ----- Ageing (A.5) -----
    ageingTitle: 'Ageing',
    ageingBucket0: '0–30',
    ageingBucket31: '31–60',
    ageingBucket61: '61–90',
    ageingBucket90: '90+',
    ageingNegativeSince: (date: string): string => `negative since ${date}`,
    ageingTiesOk: 'Buckets add up to the balance owed.',
    ageingTiesOff: 'Ageing does not tie to the balance (check C6).',
    // ----- Journal (§B) -----
    journalTitle: (month: string): string => `Transactions — ${month}`,
    journalTitleAllTime: 'Transactions — all time',
    journalAllTime: 'All time',
    journalBackToPeriod: (month: string): string => `Back to ${month}`,
    journalFilterProperty: 'Property',
    journalFilterType: 'Type',
    journalFilterStatus: 'Status',
    journalSearchPlaceholder: 'Booking id, pi_/re_/du_/tr_, unit, reference, note',
    journalOrderAsc: 'Oldest first',
    journalOrderDesc: 'Newest first',
    journalEmpty: (account: string, month: string, opening: string): string =>
        `No money moved for ${account} in ${month}. Opening balance ${opening} carried to closing.`,
    journalEmptyFiltered: 'No entries match these filters.',
    journalLoadError: 'Could not load transactions.',
    journalOpeningLine: 'Opening balance carried forward',
    journalClosingLine: 'Closing balance',
    journalRunningNet:
        'Account running balance (net) — positives and negatives across every property netted; the KPI tiles keep them split.',
    journalSubtotals: (n: number): string => `Subtotals · ${n} ${n === 1 ? 'entry' : 'entries'}`,
    journalColDate: 'Date',
    journalColProperty: 'Property',
    journalColBooking: 'Booking',
    journalColType: 'Type',
    journalColWho: 'Unit / resident',
    journalColGross: 'Gross',
    journalColRefund: 'Refund',
    journalColChargeback: 'Chargeback',
    journalColStripeFee: 'Stripe fee',
    journalColCommission: 'Commission',
    journalColClerkage: 'Cancellation charge',
    journalColPayout: 'Payout',
    journalColEffect: 'Effect',
    journalColRunning: 'Running balance',
    journalColRef: 'Stripe ref',
    journalColRecordedBy: 'Recorded by',
    journalColNote: 'Note',
    journalTypeCollected: 'Collected',
    journalTypeRefund: 'Refund',
    journalTypeCancellationCharge: 'Cancellation charge',
    journalTypeChargeback: 'Chargeback',
    journalTypeChargebackWon: 'Chargeback won',
    journalTypePayout: 'Payout',
    journalTypePayoutReversed: 'Payout reversed',
    journalTypePayoutFailed: 'Payout failed',
    // CS_SPEC §F journal additions.
    journalTypeAdjustment: 'Adjustment',
    journalTypePayoutVoided: 'Payout voided',
    journalTypeNote: 'Note',
    journalShowNotes: 'Show notes',
    journalVoidedChip: (date: string): string => `Voided ${date}`,
    journalStatusUnmeasured: 'Unmeasured only',
    journalStatusOpenDispute: 'Open disputes',
    journalStatusPendingFailed: 'Pending/Failed payouts',
    journalUtcTip: (utc: string): string => `${utc} UTC`,
    // ----- Bulk pay dialog (§F verbatim) -----
    bulkPayTitle: (account: string, amount: string): string => `Pay ${account} ${amount} via Stripe`,
    bulkPayIntro: (n: number): string =>
        `This sends ${n} ${n === 1 ? 'transfer' : 'transfers'} from Swiftlane's Stripe balance, one per property, for each property's full balance:`,
    bulkPayColProperty: 'Property',
    bulkPayColDestination: 'Stripe destination',
    bulkPayColAmount: 'Amount',
    bulkPayColPaidOn: 'Paid on',
    bulkPayExcluded: (list: string): string => `Properties not included: ${list}.`,
    bulkPayExcludedUnmeasured: 'unmeasured fees',
    bulkPayExcludedNegative: 'negative balance',
    bulkPayExcludedNoRecipient: 'no bank transfer setup',
    bulkPayExcludedRecipientPending: 'Stripe setup pending',
    bulkPayExcludedPendingTransfer: 'transfer still confirming',
    bulkPayExcludedNotCollecting: 'not collecting through Swiftlane',
    bulkPayMeasureFees: (n: number): string => `Measure fees now (${n} ${n === 1 ? 'property' : 'properties'})`,
    bulkPayMeasured: (measured: number, still: number): string => `Measured ${measured} · still unmeasured ${still}`,
    bulkPayWarning: "Stripe pays each property's bank on its normal schedule (typically 2 business days). This cannot be undone from here.",
    bulkPayRetypeLabel: 'Type the total to confirm',
    bulkPayRetypeHint: (total: string): string => `Required for totals of $1,000 or more, or 3 or more properties — type ${total}.`,
    bulkPaySend: (n: number): string => `Send ${n} ${n === 1 ? 'transfer' : 'transfers'}`,
    bulkPayNothing: 'No property is ready for a Stripe transfer.',
    bulkPayOutcomeSent: (transferId: string): string => `Sent ${transferId}`,
    bulkPayOutcomeReplayed: (transferId: string): string => `Already sent ${transferId}`,
    bulkPayOutcomePending: 'Pending — confirming with Stripe',
    bulkPayOutcomeRefused: (reason: string): string => `Refused — ${reason}`,
    bulkPayOutcomeFailed: (message: string): string => `Failed — ${message}`,
    bulkPayFooter: (amount: string, n: number): string => `Sent ${amount} in ${n} ${n === 1 ? 'transfer' : 'transfers'} · Balance updated`,
    bulkPayFailedRequest: 'Could not send the transfers.',
    bulkPayRetry: 'Retry',
    bulkPayClose: 'Close',
    // ----- Export dialogs (§D / §F) -----
    exportAllAccountsXlsx: 'All accounts workbook (.xlsx)',
    exportAccountXlsx: 'Account workbook (.xlsx)',
    exportXlsxDesc: 'Summary, Properties, Payouts, Transactions, Checks and Consolidation sheets with live SUM formulas.',
    exportRegisterCsv: 'Register CSV',
    exportLedgerCsv: 'Ledger CSV',
    exportPeriodLine: (period: string): string => `Period: ${period}`,
    exportTooLarge: 'The workbook is too large to build in time — use the CSV exports instead.',
    // ----- Payment setup tab (regrouped) -----
    setupColumnAccount: 'Account',
    setupColumnProperty: 'Property',
    setupColumnStripeAccountId: 'Stripe account id',
    setupColumnKind: 'Kind',
    setupColumnChargesEnabled: 'Charges enabled',
    setupColumnPayoutsEnabled: 'Payouts enabled',
    setupColumnConnected: 'Connected',
    setupColumnRevoked: 'Revoked',
    setupCounts: (stripe: number, pending: number, manual: number, direct: number, notSetUp: number): string =>
        `Stripe ${stripe} · Pending ${pending} · Manual ${manual} · Stripe-direct ${direct} · Not set up ${notSetUp}`,
    setupPropertiesCounts: (platform: number, connect: number, notSetUp: number): string => `${platform}/${connect}/${notSetUp} properties`,
    setupSearchPlaceholder: 'Search account or property',
    setupYes: 'Yes',
    setupNo: 'No',

    // =====================================================================================
    // CUSTOMER-SERVICE layer (CS_SPEC.md §F — copy verbatim).
    // =====================================================================================
    // ----- find anything (§B.1) -----
    searchGlobalPlaceholder: 'Search booking id, resident, unit, property, Stripe id, amount…',
    searchNoMatch: (q: string): string =>
        `Nothing matches \`${q}\`. Try a booking id, an email, or a Stripe id (pi_, re_, du_, tr_, acct_).`,
    searchSeeAll: 'See all',
    searchPageTitle: (q: string): string => `Search — ${q}`,
    searchInterpretedAs: (branch: string): string => `Interpreted as ${branch}`,
    searchTooShort: 'Type at least 2 characters.',
    searchLoadError: 'Search is unavailable right now.',
    searchGroupBooking: 'Bookings',
    searchGroupPayout: 'Payouts',
    searchGroupProperty: 'Properties',
    searchGroupAccount: 'Accounts',
    searchGroupResident: 'Residents',
    searchGroupUnit: 'Units',
    searchGroupAction: 'Support actions',
    // ----- support queue (§B.3) -----
    tabSupportQueue: 'Support queue',
    queueEmpty: 'Nothing needs support right now.',
    queueLoadError: 'Could not load the support queue.',
    queueColumnKind: 'Kind',
    // UX review #12: the cells render Property bold over Account grey — the header agrees.
    queueColumnWhere: 'Property · Account',
    queueColumnAmount: 'Amount',
    queueColumnAge: 'Age',
    queueColumnDetail: 'Detail',
    queueColumnAction: 'Action',
    queueGroupBookings: (n: number): string => `${n} bookings`,
    queueGroupEach: 'each',
    queueKindRefundFailed: 'Refund failed',
    queueKindReconcile: 'Reconcile candidate',
    queueKindOrphan: 'Orphan capture',
    queueKindDispute: 'Open dispute',
    queueKindPendingTransfer: 'Pending transfer',
    queueKindRequested: 'Awaiting approval',
    queueKindAdjustment: 'Recent adjustment',
    queueKindVoid: 'Recent void',
    queueRetryAll: (n: number): string => `Retry all (${n})`,
    queueAge: (hours: number): string => (hours < 48 ? `${Math.round(hours)}h` : `${Math.round(hours / 24)}d`),
    // Staging UX review #4 — queue rows are sentences with levers, not raw internals.
    queueDetailReconcile: 'Marked failed here, but Stripe shows a payment went out — reconcile to match the two.',
    queueActionOpen: 'Open',
    queueGroupLine: (n: number): string => `${n} identical items — expand to see each property`,
    // ----- approvals (§D.0 / §F) -----
    approvalsTitle: 'Pending approvals',
    approvalsEmpty: 'No actions are waiting for a second super admin.',
    approvalsLine: (kind: string, amount: string, property: string, who: string, age: string): string =>
        `${kind} ${amount} on ${property} — requested by ${who} ${age} ago`,
    approvalsAwaiting: 'Awaiting a second super admin',
    approve: 'Approve',
    reject: 'Reject',
    rejectReasonLabel: 'Reason for rejecting',
    approveDone: 'Approved and executed.',
    rejectDone: 'Rejected.',
    errSelfApproval: 'You requested this action — a different super admin must approve it.',
    errNotRequested: 'This request is no longer waiting for approval.',
    errExpired: 'This request expired (7 days) — request it again.',
    actionKindRetryRefund: 'Retry refund',
    actionKindRefund: 'Refund',
    actionKindReconcile: 'Reconcile',
    actionKindVoid: 'Void payout',
    actionKindReverse: 'Reverse transfer',
    actionKindAdjustment: 'Adjustment',
    actionKindExpressLogin: 'Express login email',
    actionKindNote: 'Note',
    actionStatusRequested: 'Requested',
    actionStatusExecuted: 'Executed',
    actionStatusRejected: 'Rejected',
    actionStatusExpired: 'Expired',
    actionStatusFailed: 'Failed',
    // ----- story (§C / §F) -----
    storyTitle: (id: string): string => `Booking …${id.slice(-4)}`,
    storyLoadError: 'Could not load this booking.',
    storyNoMoney: 'No money moved for this booking.',
    storyPositionTitle: 'Position today',
    storyPositionShare: 'Property share',
    storyPositionRefundable: 'Still refundable',
    storyPositionPaidOut: 'Paid out',
    storyPositionUnpaid: 'Unpaid',
    storyPositionConnect: 'Own Stripe account — Stripe paid the property directly; nothing is owed or refundable from Swiftlane.',
    storyPositionPayoutLine: (date: string, method: string, ref: string, covered: string): string =>
        `${date} · ${method} · ${ref} · ${covered}`,
    storyCopyLine: 'Copy for reply',
    storyCopyFull: 'Copy full story',
    storyAudienceResident: 'Resident',
    storyAudienceProperty: 'Property',
    storySupport: 'Support',
    storyNotes: 'Notes',
    storyTimeline: 'Timeline',
    storyDuplicateOf: (ids: string): string => `Possible duplicate of ${ids}`,
    storyDisputeTitle: 'Dispute',
    storyDisputeEvidenceDue: (date: string): string => `evidence due ${date}`,
    storyDisputeOpenInStripe: 'Open in Stripe',
    storyDisputeCached: 'Stripe status may be up to 60s old',
    storySlotUtc: (utc: string): string => `${utc} UTC`,
    actorResident: 'Resident',
    actorAdmin: 'Property admin',
    actorSystem: 'System',
    actorSupport: 'Swiftlane support',
    actorStripe: 'Stripe',
    // ----- retry refund (§F verbatim) -----
    retryRefundTitle: (amount: string, resident: string): string => `Retry the ${amount} refund to ${resident}?`,
    retryRefundBody: (failure: string): string =>
        `The earlier attempt failed: \`${failure}\`. This asks Stripe again for the same amount. If it fails again the booking stays flagged.`,
    retryRefundButton: 'Retry refund',
    retryRefundDone: (status: string): string => `Refund ${status.toLowerCase()}.`,
    // ----- refund (§F verbatim) -----
    refundTitle: (resident: string, bookingId: string): string => `Refund ${resident} on booking …${bookingId.slice(-4)}`,
    refundGoodwill: 'Goodwill refund',
    refundWaive: (kept: string): string => `Waive the cancellation charge (${kept} kept)`,
    refundUpTo: (amount: string): string => `Up to ${amount} can still be refunded`,
    refundBearerLabel: 'Who bears it',
    refundBearerProperty: (property: string): string => `The property (reduces ${property}'s share)`,
    refundBearerSwiftlane: "Swiftlane absorbs it (the property's share is unchanged)",
    refundReasonHint: "Shown in the property's journal",
    refundConfirm: (amount: string, resident: string): string =>
        `Refund ${amount} to ${resident}'s card via Stripe. Stripe returns it in 5–10 business days. The booking is not cancelled. This cannot be undone from here.`,
    refundSend: 'Send refund',
    requestApproval: 'Request approval',
    waitingSecondAdmin: 'Waiting for a second super admin.',
    refundDone: (ref: string): string => `Refund sent — ${ref}. Stripe returns it in 5–10 business days.`,
    errConnectCapture: 'Ask the property to refund from their Amenity Payments page or Stripe dashboard; the webhook records it here.',
    errDisputed: 'This charge is disputed — nothing can be refunded on it.',
    errOverRefundable: (refundable: string): string => `Only ${refundable} can still be refunded on this booking.`,
    errOverClerkage: (clerkage: string): string => `The cancellation charge kept was ${clerkage} — a waiver cannot exceed it.`,
    errRefundInFlight: 'A refund is already in flight on this booking — wait a minute and reload.',
    errNotRefundFailed: 'The newest refund on this booking has not failed — there is nothing to retry.',
    errRateLimited: 'Too many money actions in the last hour — try again later.',
    errStripeNothingRefunded: 'Nothing was refunded; the failed attempt is recorded.',
    errStripeNothingReversed: 'Nothing was reversed.',
    errBalanceUnavailable: "This property's balance could not be computed — no action can run on it.",
    // ----- reconcile (§D.3) -----
    reconcileNow: 'Reconcile now',
    reconcileDone: (action: string, state: string): string => `Reconciled — ${action} · ${state}`,
    errNoPaymentToReconcile: 'This booking has no Stripe payment to reconcile.',
    errReconcileFailed: 'Reconcile could not complete — Stripe did not answer.',
    // ----- void payout (§F verbatim) -----
    voidTitle: 'Void this payout record?',
    voidBody: (method: string, amount: string, paidOn: string, reference: string, by: string, property: string): string =>
        `${method} ${amount} · paid on ${paidOn} · ref ${reference} · recorded by ${by}. Voiding marks the record as never counted and adds ${amount} back to ${property}'s balance. The row is kept and shown struck through. No money moves — if the bank transfer really went out, do not void it.`,
    voidRetypeLabel: 'Type VOID to confirm',
    voidButton: 'Void record',
    voidDone: (amount: string): string => `Voided. ${amount} is back on the balance.`,
    errStripeRowNotVoidable: 'Reverse the transfer instead — Stripe moved this money.',
    errAlreadyVoided: 'This record is already voided.',
    errNotSettled: 'Only a settled record can be changed.',
    reasonRequired: 'A reason is required.',
    reasonLabel: 'Reason',
    // ----- reverse transfer (§F verbatim) -----
    reverseTitle: (transferId: string, amount: string): string => `Reverse ${transferId} (${amount})`,
    reverseAmountLabel: 'Amount to reverse',
    reverseAmountHint: (remaining: string): string => `Up to ${remaining} can be reversed.`,
    reverseOldBanner: (days: number): string =>
        `Stripe already paid this to the property's bank (transfer is ${days} days old). Reversing pulls the property's Stripe balance negative and Stripe may debit their bank or refuse. Prefer asking the property to pay it back — their balance here already shows the overpayment.`,
    reverseAskInstead: 'Ask the property to pay it back',
    reverseAskInsteadHint: 'No Stripe call. Copy the drafted email; record the repayment as an adjustment when it arrives.',
    reverseAnyway: 'Reverse via Stripe anyway',
    reverseBody: (amount: string, property: string): string =>
        `Stripe takes ${amount} back from ${property}'s connected balance into Swiftlane's. This cannot be undone from here.`,
    reverseButton: (amount: string): string => `Reverse ${amount}`,
    reverseDone: (amount: string): string => `Reversed ${amount}. Balance updated.`,
    reverseDraftEmail: (amount: string, date: string, property: string): string =>
        `Hello ${property} team — on ${date} we paid you ${amount} in error. Your amenity balance with Swiftlane now shows this overpayment; please wire ${amount} back to Swiftlane and reply with the reference so we can record the repayment. Thank you.`,
    reverseCopyDraft: 'Copy email draft',
    errNotStripe: 'Only a Stripe transfer can be reversed — void a manual record instead.',
    errOverReversible: (remaining: string): string => `Only ${remaining} of this transfer can still be reversed.`,
    errReversalInFlight: 'A reversal is already in progress on this transfer.',
    // ----- adjustment (§F verbatim) -----
    adjustmentTitle: (property: string): string => `Adjust ${property}'s balance`,
    adjustmentCredit: 'Credit',
    adjustmentDebit: 'Debit',
    adjustmentDirectionHint: 'Credit = Swiftlane owes the property more',
    adjustmentCategory: 'Category',
    adjustmentBookingId: 'Booking id (optional)',
    adjustmentReference: 'Reference (optional)',
    adjustmentPreview: (from: string, to: string): string => `Balance ${from} → ${to}`,
    adjustmentConfirm: 'This adds a signed line to the ledger. It cannot be edited; correct it with an opposite adjustment.',
    adjustmentButton: 'Add adjustment',
    adjustmentDone: 'Adjustment recorded. Balance updated.',
    categoryWriteOff: 'Write off negative balance',
    categoryGoodwill: 'Goodwill',
    categoryCorrection: 'Correction',
    categoryDisputeManual: 'Dispute (manual)',
    categoryRepayment: 'Repayment received',
    categoryOther: 'Other',
    errOtherReasonLength: 'For "Other", the reason must be at least 20 characters.',
    errWriteOffNotNegative: 'The balance is not negative — nothing to write off.',
    errWriteOffOverNegative: (negative: string): string => `A write-off cannot exceed the ${negative} negative balance.`,
    errNotPlatform: 'Adjustments apply only to properties Swiftlane collects for.',
    // ----- notes (§D.8) -----
    notesTitle: (n: number): string => `Notes (${n})`,
    noteInternal: 'Internal — never shown to properties or residents.',
    notePlaceholderCs: 'Add an internal note (up to 2,000 characters)',
    noteAdd: 'Add note',
    notePin: 'Pin',
    noteRetract: 'Retract',
    noteRetracted: 'retracted',
    notePinned: 'Pinned',
    noteEmpty: 'No notes yet.',
    noteLoadError: 'Could not load notes.',
    // ----- bank setup helpers (§D.7) -----
    supportMenu: 'Support',
    copySetupLink: 'Copy setup link',
    copySetupLinkDone: 'Setup link copied.',
    emailExpressLogin: 'Email Express dashboard login',
    emailExpressLoginDone: (n: number): string => `Login link emailed to ${n} ${n === 1 ? 'admin' : 'admins'}.`,
    errNotExpress: 'They log in to their own Stripe — no Express dashboard link exists for this account.',
    bankSetupHelpers: 'Bank setup helpers',
    openStory: 'Payment story',
    addNote: 'Add note',
    voidMenu: 'Void',
    reverseMenu: 'Reverse',
    adjustmentMenu: 'Adjustment',
    voidedChip: (date: string, by: string): string => (by ? `Voided ${date} · ${by}` : `Voided ${date}`),
    rfAdjustments: 'Adjustments',
    rfPayoutsVoided: 'Payouts voided (recorded in period)',
};
